import { Card } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import styles from '../styles/Activity.module.scss';
import TitleContainer from '../TitleContainer';
import { RiskAssessmentState } from '@components/Activity/types/RiskAssessmentState';
import RiskAtexZone from './RiskAtexZone';
import RiskTableContainer from './RiskTableContainer';
import RiskCheckboxSection from './RiskCheckboxSection ';
import RiskRadiobuttonSection from './RiskRadiobuttonSection';
import AddContactsList from '../AddContactsList';
import TextField from './TextField';
import { useFormContext } from 'react-hook-form';

type Props = {
  riskAssessment: RiskAssessmentState;
  disabledComponent?: boolean;
  mode?: string;
};

const RiskAssessmentDetailsContainer = ({ riskAssessment, disabledComponent, mode }: Props) => {
  const { t } = useTranslation();

  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <div>
      <div>
        <Card className={disabledComponent ? `${styles.cardOverview}` : `${styles.card}`}>
          <TitleContainer header="risk.assessment.details.page.risk.details.container.title" />
          <RiskCheckboxSection
            titleKey="risk.assessment.details.page.risk.details.container.checkbox.title.category.of.substance"
            items={riskAssessment?.categoryItems}
            state={riskAssessment?.flammableCategory}
            setState={riskAssessment?.setFlammableCategory}
            text={riskAssessment?.flammableCategory.otherTxt}
            setText={riskAssessment.setTextOther}
            checkboxKeyPrefix={
              'risk.assessment.details.page.risk.details.container.checkbox.category'
            }
            otherKey="other"
            placeholderOther={t(
              'risk.assessment.details.page.risk.details.container.checkbox.category.placeholderOther'
            )}
            disabled={disabledComponent}
          />
          <RiskCheckboxSection
            titleKey="risk.assessment.details.page.risk.details.container.checkbox.title.affected.group"
            items={riskAssessment?.items}
            state={riskAssessment.affectedGroup}
            setState={riskAssessment.setAffectedGroup}
            text={riskAssessment?.affectedGroup?.otherTxt}
            setText={riskAssessment.setTextOtherGroup}
            checkboxKeyPrefix={
              'risk.assessment.details.page.risk.details.container.checkbox.affected.group'
            }
            otherKey="other"
            placeholderOther={t(
              'risk.assessment.details.page.risk.details.container.checkbox.affected.group.placeholderOther'
            )}
            disabled={disabledComponent}
          />
          <div className={styles.inlineCards}>
            <div className={styles.oneCard}>
              <RiskAtexZone
                atexZone={riskAssessment.atexZone}
                setAtexZone={riskAssessment.setAtexZone}
                disabled={disabledComponent}
              />
            </div>
            <RiskRadiobuttonSection
              header={'risk.assessment.details.page.risk.details.container.checkbox.title.esi'}
              value={riskAssessment?.esiValue}
              setValue={riskAssessment.setEsiValue}
              edmsNo
              text={riskAssessment.esiEdms}
              setText={riskAssessment.setEsiEdms}
              placeholder={t(
                'risk.assessment.details.page.risk.details.container.checkbox.esi.placeholder'
              )}
              name={'esi'}
              disabled={disabledComponent}
            />
          </div>
          <RiskCheckboxSection
            items={riskAssessment?.measuresItems}
            state={riskAssessment.preventionMeasures}
            setState={riskAssessment.setPreventionMeasures}
            text={riskAssessment?.preventionMeasures?.otherTxt}
            setText={riskAssessment.setTextOtherMeasures}
            titleKey={'risk.assessment.details.page.risk.details.container.checkbox.title.measures'}
            checkboxKeyPrefix={
              'risk.assessment.details.page.risk.details.container.checkbox.measures'
            }
            otherKey="other"
            placeholderOther={t(
              'risk.assessment.details.page.risk.details.container.checkbox.measures.placeholderOther'
            )}
            disabled={disabledComponent}
          />
          <div>
            <RiskTableContainer
              riskAssessment={riskAssessment}
              disabledComponent={disabledComponent}
              mode={mode}
            />
          </div>
          <div className={styles.inlineCards}>
            <RiskRadiobuttonSection
              header={
                'risk.assessment.details.page.risk.details.container.checkbox.title.leakTested'
              }
              value={riskAssessment.leakTested}
              setValue={riskAssessment.setLeakTested}
              naValue
              name={'leakTest'}
              disabled={disabledComponent}
            />
            <RiskRadiobuttonSection
              header={
                'risk.assessment.details.page.risk.details.container.checkbox.title.warningNotices'
              }
              value={riskAssessment.warningNotices}
              setValue={riskAssessment.setWarningNotices}
              name={'warningNotices'}
              disabled={disabledComponent}
            />
          </div>
          <div>
            <TextField
              header={t(
                'risk.assessment.details.page.risk.details.container.additionalInformation.title'
              )}
              text={riskAssessment.additionalText}
              setText={riskAssessment.setAdditionalText}
              disabled={disabledComponent}
              formregistor={register('additionalInformation', {
                maxLength: {
                  value: 4000,
                  message: t('activity.form.error.field.max.val.4000'),
                },
              })}
              error={errors?.additionalInformation?.message?.toString()}
            />
          </div>
          <div>
            <TextField
              header={t('risk.assessment.details.page.risk.details.container.workEquipment.title')}
              text={riskAssessment.equipmentText}
              setText={riskAssessment.setEquipmentText}
              disabled={disabledComponent}
              formregistor={register('workEquipmentInformation', {
                maxLength: {
                  value: 4000,
                  message: t('activity.form.error.field.max.val.4000'),
                },
              })}
              error={errors?.workEquipmentInformation?.message?.toString()}
            />
          </div>
          <RiskCheckboxSection
            items={riskAssessment?.organisationalItems}
            state={riskAssessment.organisationalMeasures}
            setState={riskAssessment.setOrganisationalMeasures}
            titleKey={
              'risk.assessment.details.page.risk.details.container.checkbox.title.organisationalMeasures'
            }
            checkboxKeyPrefix={
              'risk.assessment.details.page.risk.details.container.checkbox.organisationalMeasures'
            }
            edms={
              riskAssessment?.riskAssessmentContainer?.organisationalMeasures
                ?.edmsOrganisationalMeasures
            }
            setEdms={riskAssessment.setOrganisationalMeasuresEdms}
            placeholder={t(
              'risk.assessment.details.page.risk.details.container.checkbox.organisationalMeasures.placeholder'
            )}
            disabled={disabledComponent}
          />
          <RiskCheckboxSection
            items={riskAssessment?.mitigationItems}
            state={riskAssessment.mitigationMeasures}
            setState={riskAssessment.setMitigationMeasures}
            text={riskAssessment?.mitigationMeasures?.otherTxt}
            setText={riskAssessment.setTextOtherMitigation}
            titleKey={
              'risk.assessment.details.page.risk.details.container.checkbox.title.mitigation'
            }
            checkboxKeyPrefix={
              'risk.assessment.details.page.risk.details.container.checkbox.mitigation'
            }
            otherKey="other"
            placeholderOther={t(
              'risk.assessment.details.page.risk.details.container.checkbox.mitigation.placeholderOther'
            )}
            disabled={disabledComponent}
          />

          <RiskCheckboxSection
            items={riskAssessment?.workerTrainingInformationItems}
            state={riskAssessment.workerTrainingInformation}
            setState={riskAssessment.setWorkerTrainingInformation}
            text={riskAssessment?.workerTrainingInformation?.otherTxt}
            setText={riskAssessment.setTextOtherWorkerTrainingInformation}
            titleKey={
              'risk.assessment.details.page.risk.details.container.checkbox.title.workerTrainingInformation'
            }
            checkboxKeyPrefix={
              'risk.assessment.details.page.risk.details.container.checkbox.workerTrainingInformation'
            }
            otherKey="other"
            placeholderOther={t(
              'risk.assessment.details.page.risk.details.container.checkbox.workerTrainingInformation.placeholderOther'
            )}
            disabled={disabledComponent}
          />
          {/* <AddContactsList
            contactList={riskAssessment.exposedPersonnel}
            setContactList={riskAssessment.setExposedPersonnel}
            title={t('risk.assessment.details.page.risk.details.container.exposedPersonnel.title')}
            placeholder={
              'risk.assessment.details.page.risk.details.container.exposedPersonnel.placeholder'
            }
            disabled={disabledComponent}
            isNotMissingLmsCourses={false}
          /> */}
        </Card>
      </div>
    </div>
  );
};

export default RiskAssessmentDetailsContainer;
